import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Typography,
} from "@mui/material";
import { AttachFile, CheckCircleOutline, Close, ErrorOutline } from "@mui/icons-material";
import DropZone from "../../../components/DropZone";
import { useUploadBulkPayments } from "../api/paymentMutations";
import type { BulkUploadResult } from "../api/paymentMutations";
import { paymentKeys } from "../api/queryKeys";
import { parseUploadErrors } from "../utils/formatters";
import type { UploadErrorGroup } from "../utils/formatters";

interface UploadResult {
  processedCount: number;
  errorGroups: UploadErrorGroup[];
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function BulkPaymentUploadModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();
  const uploadMutation = useUploadBulkPayments();
  const [result, setResult] = useState<UploadResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const processFile = useCallback(
    (file: File) => {
      if (uploadMutation.isLoading) return;
      // accept=".csv" only constrains the file picker — the drop path has no filter
      if (!/\.csv$/i.test(file.name)) return;
      setFileName(file.name);
      setResult(null);
      const formData = new FormData();
      formData.append("file", file);
      uploadMutation.mutate(formData, {
        onSuccess: (res: BulkUploadResult) => {
          const errorGroups = parseUploadErrors(res.errors);
          setResult({ processedCount: res.processed_requests, errorGroups });
          queryClient.invalidateQueries(paymentKeys.all);
        },
        onError: () => {
          setResult(null);
        },
      });
    },
    [uploadMutation, queryClient],
  );

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      if (files[0]) processFile(files[0]);
    },
    [processFile],
  );

  const handleClose = () => {
    if (uploadMutation.isLoading) return;
    onClose();
  };

  const hasContent = result !== null || uploadMutation.isError || uploadMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Typography fontSize="16px" fontWeight={600}>Upload Payments</Typography>
        <IconButton size="small" onClick={handleClose} disabled={uploadMutation.isLoading} aria-label="Close" data-testid="upload-close-x">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Drop zone */}
        <Box mb={hasContent ? 2 : 0}>
          <DropZone
            onFilesSelected={handleFilesSelected}
            accept=".csv"
            title="Drop CSV file here or click to browse"
            subtitle="CSV &middot; 1 file per upload"
            ariaLabel="Select CSV file"
            buttonLabel={uploadMutation.isLoading ? "Uploading\u2026" : undefined}
            multiple={false}
            disabled={uploadMutation.isLoading}
          />
          {fileName && (
            <Box mt={1} textAlign="center">
              <Chip
                icon={<AttachFile sx={{ fontSize: 14 }} />}
                label={fileName}
                size="small"
                sx={{ fontSize: "12px", maxWidth: 320 }}
              />
            </Box>
          )}
        </Box>

        {/* Network / API-level error */}
        {uploadMutation.isError && !result && (
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorOutline fontSize="small" color="error" />
            <Typography variant="body2" color="error">
              {(uploadMutation.error as any)?.message ?? "Upload failed — please try again."}
            </Typography>
          </Box>
        )}

        {/* Upload results */}
        {result && (
          <Box>
            {/* Success row */}
            {result.processedCount > 0 && (
              <Box display="flex" alignItems="center" gap={1} mb={result.errorGroups.length > 0 ? 1.5 : 0}>
                <CheckCircleOutline fontSize="small" sx={{ color: "success.main" }} />
                <Typography variant="body2" color="success.main">
                  {result.processedCount} payment request(s) recorded successfully.
                </Typography>
              </Box>
            )}

            {/* Error rows */}
            {result.errorGroups.length > 0 && (
              <>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ErrorOutline fontSize="small" color="error" />
                  <Typography variant="body2" color="error">
                    {result.errorGroups.length} request(s) could not be processed:
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {result.errorGroups.map((g, i) => (
                    <Box
                      key={i}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: "4px",
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                        {g.requestId === "?" ? "File-level errors" : `Payment Request #${g.requestId}`}
                      </Typography>
                      {g.rowErrors.map((err, j) => (
                        <Typography key={j} variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                          {err}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {result.processedCount === 0 && result.errorGroups.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No records to process.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClose}
          disabled={uploadMutation.isLoading}
          data-testid="upload-close-btn"
          sx={{ textTransform: "none", height: 32 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
